import { Injectable } from '@angular/core';
import { NzMessageService } from 'ng-zorro-antd/message';
import { map, Observable, take } from 'rxjs';
import { LangchainPublicGate } from '../../../../../backend/src/core/langchain/langchain.public.gate';
import {
  CreateMessageRequest,
  CreateRunRequest,
  CreateThreadRequest,
  GetAnswerRequest,
  ListMessagesRequest,
  OpenaiGate,
  RetrieveMessageRequest,
  RetrieveRunRequest,
  StreamingChatRequest,
  TextToSpeechRequest,
} from '../../../../../backend/src/core/openai/openai.gate';
import { UrlResponse } from '../../../../../backend/src/core/request-response';
import { ResourceName } from '../../../../../backend/src/core/resource-name';
import { LambdaService } from '../../shared/core/lambda.service';
import { BoardHolderService } from './../board/board.holder.service';
import { WorkspaceHolderService } from './../workspace/workspace.holder.service';
import { OpenaiAssistant, OpenaiMessage } from './openai';

@Injectable({
  providedIn: 'root',
})
export class OpenaiService {
  constructor(
    private lambdaService: LambdaService,
    private boardHolderService: BoardHolderService,
    private workspaceHolderService: WorkspaceHolderService,
    private msg: NzMessageService
  ) {}

  getLangchain() {
    this.lambdaService
      .run$(ResourceName.langchain, LangchainPublicGate.actions.callOpenAi, {}, { isPublicUrl: true })
      .pipe(take(1))
      .subscribe();
  }

  textToSpeech$(
    coreId: string,
    input: string,
    config?: { additionalId?: string | number; voice?: 'fable' | 'alloy' | 'echo' | 'onyx' | 'nova' | 'shimmer' }
  ) {
    let voice = config?.voice;
    if (!voice) {
      voice = 'fable';
    }
    const request: TextToSpeechRequest = {
      coreId,
      additionalId: config?.additionalId,
      personalBoardId: this.boardHolderService.myPersonalBoard()!.id,
      workspaceId: this.workspaceHolderService.currentWorkspace().id,

      input,
      model: 'tts-1',
      voice: voice!,
    };

    return this.lambdaService
      .run$(ResourceName.openai, OpenaiGate.actions.textToSpeech, request, { notChangeLoadingStatus: true })
      .pipe(
        map((data: UrlResponse) => {
          return data.url;
        })
      );
  }

  streamingChat$(
    coreId: string,
    messageList: OpenaiMessage[],
    config?: { model?: string; temperature?: number; additionalId?: string }
  ) {
    const request: StreamingChatRequest = {
      coreId,
      additionalId: config?.additionalId,
      personalBoardId: this.boardHolderService.myPersonalBoard()!.id,
      workspaceId: this.workspaceHolderService.currentWorkspace().id,
      messageList: messageList,
      config: {
        model: config?.model,
        temperature: config?.temperature,
      },
    };

    return this.lambdaService.run$(ResourceName.openai, OpenaiGate.actions.streamingChat, request);
  }

  getAnswer$(request: GetAnswerRequest, notChangeLoadingStatus = false): Observable<string | any> {
    if (!request.config?.openaiModel && !request.config?.groqModel) {
      throw new Error('openaiModel or groqModel is required');
    }

    return this.lambdaService
      .run$(ResourceName.openai, OpenaiGate.actions.getAnswer, request, {
        notChangeLoadingStatus,
      })
      .pipe(
        map((data) => {
          if (data?.answer?.tool_calls) {
            if (data?.answer?.tool_calls.length > 0) {
              return JSON.parse(data.answer.tool_calls[0].function.arguments);
            }
          } else if (data?.answer?.content) {
            const content = data.answer.content as string;
            if (content.length > 0 && content[0] === '"' && content[content.length - 1] === '"') {
              return (data.answer.content as string).slice(1, -1);
            }

            return data.answer.content as string;
          } else {
            return '';
          }
        })
      );
  }

  createAssistant$(assistant: Partial<OpenaiAssistant>) {
    return this.lambdaService.run$(ResourceName.openai, OpenaiGate.actions.createAssistant, assistant);
  }

  createThread$(request?: CreateThreadRequest) {
    return this.lambdaService.run$(ResourceName.openai, OpenaiGate.actions.createThread, request);
  }

  createMessage$(request: CreateMessageRequest) {
    return this.lambdaService.run$(ResourceName.openai, OpenaiGate.actions.createMessage, request);
  }

  retrieveMessage$(request: RetrieveMessageRequest) {
    return this.lambdaService.run$(ResourceName.openai, OpenaiGate.actions.retrieveMessage, request);
  }

  listMessages$(request: ListMessagesRequest) {
    return this.lambdaService.run$(ResourceName.openai, OpenaiGate.actions.listMessages, request);
  }

  retrieveRun$(request: RetrieveRunRequest) {
    return this.lambdaService.run$(ResourceName.openai, OpenaiGate.actions.retrieveRun, request, {
      notChangeLoadingStatus: true,
    });
  }

  createRun$(request: CreateRunRequest) {
    return this.lambdaService.run$(ResourceName.openai, OpenaiGate.actions.createRun, request);
  }
}
